import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import { useAuthStore } from '../store/auth.store'
import { PlanType } from '@teffy/shared-types'
import {
  PageHeader, Card, Button, Field, Input,
} from '../components/ui'

interface Restaurant {
  id: string; name: string; address: string | null; logoUrl: string | null
  venueType: 'RESTAURANT' | 'HOTEL' | 'APARTMENT' | 'HOSPITAL'
  eventModeActive: boolean
  showRemainingStock: boolean
  lowStockThreshold: number | null
  contractedDishes: number | null
  selfService: boolean
  requireTableActivation: boolean
  takeoutNameHint: string | null
  valetEnabled: boolean
  waitlistEnabled: boolean
  roomChargeEnabled: boolean
  loyaltyEnabled: boolean; printingEnabled: boolean
  ratingsEnabled: boolean; ratingBonusPoints: number
  reservationsEnabled: boolean
  aiAgentEnabled: boolean
  aiAgentName: string
  aiAgentTone: 'FRIENDLY' | 'FORMAL' | 'CASUAL'
  aiAgentLanguage: 'ES' | 'EN' | 'BILINGUAL'
  aiAgentHighlights: string
  aiAgentInstructions: string
  aiAgentWelcome: string
  disclaimer: string
  timezone: string
  razonSocial: string | null; rfc: string | null; regimenFiscal: string | null
  direccionFiscal: string | null; urlFacturacion: string | null
  transferAccountNumber: string | null; transferAccountHolder: string | null
  quickMessages: string[]
  hotelLocations: string[]
  serviceChargePct: number
  slug: string
  colorTheme: string
  menuLayout: string
  plan: PlanType
  planExpiresAt: string | null
}

const THEMES = [
  { id: 'obsidiana', name: 'Obsidiana', primary: '#18181B' },
  { id: 'esmeralda', name: 'Esmeralda', primary: '#047857' },
  { id: 'indigo',    name: 'Índigo',    primary: '#4338CA' },
  { id: 'pizarra',   name: 'Pizarra',   primary: '#1E3A5F' },
  { id: 'ciruela',   name: 'Ciruela',   primary: '#6D28D9' },
  { id: 'burdeos',   name: 'Burdeos',   primary: '#9F1239' },
  { id: 'mandarina', name: 'Mandarina', primary: '#C2410C' },
  { id: 'miel',      name: 'Miel',      primary: '#A16207' },
  { id: 'camelia',   name: 'Camelia',   primary: '#BE185D' },
  { id: 'tierra',    name: 'Tierra',    primary: '#78350F' },
]

const PLAN_COLOR: Record<PlanType, string> = {
  [PlanType.BASIC]: 'bg-gray-100 text-gray-600',
  [PlanType.PRO]: 'bg-blue-50 text-blue-700',
  [PlanType.ENTERPRISE]: 'bg-purple-50 text-purple-700',
}

const PLAN_FEATURES: Record<PlanType, Set<string>> = {
  [PlanType.BASIC]: new Set([]),
  [PlanType.PRO]: new Set(['ratings', 'loyalty']),
  [PlanType.ENTERPRISE]: new Set(['ratings', 'loyalty', 'reservations', 'valet', 'waitlist', 'printing', 'aiAgent', 'roomCharge']),
}

function planAllows(plan: PlanType | undefined, feature: string): boolean {
  if (!plan) return true
  return PLAN_FEATURES[plan]?.has(feature) ?? false
}

const FEATURE_UPGRADE: Record<string, string> = {
  ratings: 'Requiere plan PRO o superior',
  loyalty: 'Requiere plan PRO o superior',
  reservations: 'Requiere plan ENTERPRISE',
  valet: 'Requiere plan ENTERPRISE',
  waitlist: 'Requiere plan ENTERPRISE',
  printing: 'Requiere plan ENTERPRISE',
  aiAgent: 'Requiere plan ENTERPRISE',
  roomCharge: 'Requiere plan ENTERPRISE',
}

type Tab = 'general' | 'formato' | 'mensajes' | 'modulos' | 'ia' | 'evento' | 'fiscal' | 'cuenta'

const TABS: { id: Tab; label: string }[] = [
  { id: 'general',   label: 'General' },
  { id: 'formato',   label: 'Formato' },
  { id: 'mensajes',  label: 'Mensajes' },
  { id: 'modulos',   label: 'Módulos' },
  { id: 'ia',        label: 'IA' },
  { id: 'evento',    label: 'Evento' },
  { id: 'fiscal',    label: 'Fiscal' },
  { id: 'cuenta',    label: 'Mi cuenta' },
]

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('general')

  return (
    <div className="p-8 max-w-2xl mx-auto space-y-6">
      <PageHeader title="Configuración" subtitle="Gestiona los datos de tu restaurante y cuenta" />

      {/* Barra de pestañas */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab !== 'cuenta'
        ? <RestaurantSection activeTab={activeTab as Exclude<Tab, 'cuenta'>} />
        : <ProfileSection />
      }
    </div>
  )
}

// ─── Restaurante ──────────────────────────────────────────────────────────────
function RestaurantSection({ activeTab }: { activeTab: Exclude<Tab, 'cuenta'> }) {
  const { updateProfile } = useAuthStore()
  const qc = useQueryClient()

  const { data: restaurant, isLoading } = useQuery<Restaurant>({
    queryKey: ['restaurant'],
    queryFn: async () => { const { data } = await api.get('/restaurant'); return data },
  })

  const [form, setForm] = useState({
    name: '', address: '', logoUrl: '',
    venueType: 'RESTAURANT' as 'RESTAURANT' | 'HOTEL' | 'APARTMENT' | 'HOSPITAL',
    roomChargeEnabled: false, loyaltyEnabled: false, printingEnabled: false,
    ratingsEnabled: false, ratingBonusPoints: 0, reservationsEnabled: false,
    aiAgentEnabled: false, aiAgentName: 'Asistente',
    aiAgentTone: 'FRIENDLY' as 'FRIENDLY' | 'FORMAL' | 'CASUAL',
    aiAgentLanguage: 'ES' as 'ES' | 'EN' | 'BILINGUAL',
    aiAgentHighlights: '', aiAgentInstructions: '', aiAgentWelcome: '',
    disclaimer: 'La comisión por plataforma, financia la tecnología de pedido digital que estás usando. Este cargo es definido por el establecimiento, no por TEFFY.',
    timezone: 'America/Mexico_City',
    razonSocial: '', rfc: '', regimenFiscal: '', direccionFiscal: '', urlFacturacion: '',
    transferAccountNumber: '', transferAccountHolder: '',
    quickMessages: ['', '', '', '', '', ''] as string[],
    hotelLocations: [] as string[],
    serviceChargePct: 1.5, slug: '', eventModeActive: false,
    showRemainingStock: false, lowStockThreshold: null as number | null,
    contractedDishes: null as number | null,
    selfService: false, requireTableActivation: false, takeoutNameHint: '',
    valetEnabled: false, waitlistEnabled: false,
    colorTheme: 'obsidiana', menuLayout: 'horizontal' as 'horizontal' | 'vertical',
  })
  const [saved, setSaved] = useState(false)

  const [synced, setSynced] = useState(false)
  if (restaurant && !synced) {
    setForm({
      name: restaurant.name ?? '',
      address: restaurant.address ?? '',
      logoUrl: restaurant.logoUrl ?? '',
      loyaltyEnabled:    restaurant.loyaltyEnabled    ?? false,
      printingEnabled:   restaurant.printingEnabled   ?? false,
      ratingsEnabled:    restaurant.ratingsEnabled    ?? false,
      ratingBonusPoints: restaurant.ratingBonusPoints ?? 0,
      razonSocial:     restaurant.razonSocial     ?? '',
      rfc:             restaurant.rfc             ?? '',
      regimenFiscal:   restaurant.regimenFiscal   ?? '',
      direccionFiscal: restaurant.direccionFiscal ?? '',
      urlFacturacion:  restaurant.urlFacturacion  ?? '',
      transferAccountNumber: restaurant.transferAccountNumber ?? '',
      transferAccountHolder: restaurant.transferAccountHolder ?? '',
      venueType: restaurant.venueType ?? 'RESTAURANT',
      roomChargeEnabled: restaurant.roomChargeEnabled ?? false,
      quickMessages: Array.from({ length: 6 }, (_, i) => restaurant.quickMessages?.[i] ?? ''),
      hotelLocations: restaurant.hotelLocations ?? [],
      serviceChargePct: restaurant.serviceChargePct ?? 1.5,
      reservationsEnabled: restaurant.reservationsEnabled ?? false,
      aiAgentEnabled:      restaurant.aiAgentEnabled      ?? false,
      aiAgentName:         restaurant.aiAgentName         ?? 'Asistente',
      aiAgentTone:         restaurant.aiAgentTone         ?? 'FRIENDLY',
      aiAgentLanguage:     restaurant.aiAgentLanguage     ?? 'ES',
      aiAgentHighlights:   restaurant.aiAgentHighlights   ?? '',
      aiAgentInstructions: restaurant.aiAgentInstructions ?? '',
      aiAgentWelcome:      restaurant.aiAgentWelcome      ?? '',
      disclaimer:          restaurant.disclaimer          ?? 'La comisión por plataforma, financia la tecnología de pedido digital que estás usando. Este cargo es definido por el establecimiento, no por TEFFY.',
      timezone:            restaurant.timezone            ?? 'America/Mexico_City',
      slug: restaurant.slug ?? '',
      eventModeActive:      restaurant.eventModeActive      ?? false,
      showRemainingStock:   restaurant.showRemainingStock   ?? false,
      lowStockThreshold:    restaurant.lowStockThreshold    ?? null,
      contractedDishes:     restaurant.contractedDishes     ?? null,
      selfService:              restaurant.selfService              ?? false,
      requireTableActivation:   restaurant.requireTableActivation   ?? false,
      takeoutNameHint:          restaurant.takeoutNameHint          ?? '',
      valetEnabled:             restaurant.valetEnabled             ?? false,
      waitlistEnabled:          restaurant.waitlistEnabled          ?? false,
      colorTheme:               restaurant.colorTheme               ?? 'obsidiana',
      menuLayout:               (restaurant.menuLayout               ?? 'horizontal') as 'horizontal' | 'vertical',
    })
    setSynced(true)
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {}
      if (form.name) payload.name = form.name
      if (form.address !== undefined) payload.address = form.address
      if (form.logoUrl !== undefined) payload.logoUrl = form.logoUrl
      payload.loyaltyEnabled    = form.loyaltyEnabled
      payload.printingEnabled   = form.printingEnabled
      payload.ratingsEnabled    = form.ratingsEnabled
      payload.ratingBonusPoints = form.ratingBonusPoints
      payload.reservationsEnabled = form.reservationsEnabled
      payload.aiAgentEnabled      = form.aiAgentEnabled
      payload.aiAgentName         = form.aiAgentName
      payload.aiAgentTone         = form.aiAgentTone
      payload.aiAgentLanguage     = form.aiAgentLanguage
      payload.aiAgentHighlights   = form.aiAgentHighlights   || null
      payload.aiAgentInstructions = form.aiAgentInstructions || null
      payload.aiAgentWelcome      = form.aiAgentWelcome      || null
      payload.disclaimer          = form.disclaimer
      if (form.razonSocial    !== undefined) payload.razonSocial    = form.razonSocial
      if (form.rfc            !== undefined) payload.rfc            = form.rfc
      if (form.regimenFiscal  !== undefined) payload.regimenFiscal  = form.regimenFiscal
      if (form.direccionFiscal !== undefined) payload.direccionFiscal = form.direccionFiscal
      if (form.urlFacturacion !== undefined) payload.urlFacturacion = form.urlFacturacion
      if (form.transferAccountNumber !== undefined) payload.transferAccountNumber = form.transferAccountNumber
      if (form.transferAccountHolder !== undefined) payload.transferAccountHolder = form.transferAccountHolder
      payload.venueType = form.venueType
      payload.roomChargeEnabled = form.roomChargeEnabled
      payload.hotelLocations = form.hotelLocations.map((l) => l.trim()).filter(Boolean)
      payload.quickMessages = form.quickMessages.map((m) => m.trim()).filter(Boolean)
      payload.eventModeActive     = form.eventModeActive
      payload.showRemainingStock  = form.showRemainingStock
      payload.lowStockThreshold   = form.lowStockThreshold
      payload.contractedDishes    = form.contractedDishes
      payload.selfService             = form.selfService
      payload.requireTableActivation  = form.requireTableActivation
      payload.takeoutNameHint         = form.takeoutNameHint || null
      payload.valetEnabled            = form.valetEnabled
      payload.waitlistEnabled         = form.waitlistEnabled
      payload.colorTheme              = form.colorTheme
      payload.menuLayout              = form.menuLayout
      payload.timezone                = form.timezone
      if (form.slug && form.slug !== restaurant?.slug) payload.slug = form.slug
      const { data } = await api.patch('/restaurant', payload)
      return data
    },
    onSuccess: (data: Restaurant) => {
      qc.invalidateQueries({ queryKey: ['restaurant'] })
      updateProfile({ restaurantName: data.name })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
  })

  const SaveBar = () => (
    <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
      <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>
        Guardar cambios
      </Button>
      {saved && <span className="text-sm text-green-600 font-medium">✓ Guardado</span>}
      {mutation.isError && (
        <span className="text-sm text-red-500">{(mutation.error as Error).message}</span>
      )}
    </div>
  )

  if (isLoading) return <Card className="p-6 text-sm text-gray-400">Cargando…</Card>

  // ── Pestaña: General ──────────────────────────────────────────────────────
  if (activeTab === 'general') return (
    <Card>
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <h2 className="font-bold text-gray-900">Datos generales</h2>
        {restaurant && (
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${PLAN_COLOR[restaurant.plan]}`}>
              {restaurant.plan}
            </span>
            {restaurant.planExpiresAt && (
              <span className="text-xs text-gray-400">
                Vence {new Date(restaurant.planExpiresAt).toLocaleDateString('es-MX')}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="p-6 space-y-4">
        <Field label="Nombre del restaurante">
          <Input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Nombre del restaurante"
          />
        </Field>
        <Field label="Identificador único (slug)">
          <Input
            value={form.slug}
            onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
            placeholder="mi-gauchito-especial"
          />
          <p className="text-xs text-gray-400 mt-1">
            Solo minúsculas, números y guiones. Se usa para identificar el restaurante en la app móvil.
          </p>
        </Field>
        <Field label="Dirección">
          <Input
            value={form.address}
            onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            placeholder="Calle, número, ciudad"
          />
        </Field>
        <Field label="URL del logo">
          <Input
            value={form.logoUrl}
            onChange={(e) => setForm((f) => ({ ...f, logoUrl: e.target.value }))}
            placeholder="https://..."
          />
        </Field>

        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Zona horaria</label>
          <select
            value={form.timezone}
            onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
          >
            <option value="America/Mexico_City">Ciudad de México (CST/CDT)</option>
            <option value="America/Cancun">Cancún / Mérida (EST)</option>
            <option value="America/Chihuahua">Chihuahua / Hermosillo (MST)</option>
            <option value="America/Tijuana">Tijuana / Mexicali (PST/PDT)</option>
            <option value="America/Bogota">Bogotá / Lima / Quito (COT)</option>
            <option value="America/Santiago">Santiago (CLT/CLST)</option>
            <option value="America/Buenos_Aires">Buenos Aires (ART)</option>
            <option value="America/Caracas">Caracas (VET)</option>
            <option value="America/New_York">Nueva York (EST/EDT)</option>
            <option value="America/Los_Angeles">Los Ángeles (PST/PDT)</option>
            <option value="Europe/Madrid">Madrid (CET/CEST)</option>
          </select>
          <p className="text-xs text-gray-400 mt-1">Se usa para los horarios de disponibilidad de menús y platillos.</p>
        </div>

        <SaveBar />
      </div>
    </Card>
  )

  // ── Pestaña: Formato ─────────────────────────────────────────────────────
  if (activeTab === 'formato') return (
    <Card>
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="font-bold text-gray-900">Formato del menú</h2>
        <p className="text-xs text-gray-400 mt-0.5">Apariencia visual del menú digital del comensal</p>
      </div>
      <div className="p-6 space-y-6">
        {/* Layout */}
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-1">Presentación de platillos</p>
          <p className="text-xs text-gray-400 mb-3">Cómo se muestran las tarjetas en el menú digital del comensal</p>
          <div className="flex gap-3">
            {([
              { value: 'horizontal', label: 'Horizontal', desc: 'Texto a la izquierda, imagen a la derecha', icon: '▬▮' },
              { value: 'vertical',   label: 'Vertical',   desc: 'Imagen arriba, texto abajo',               icon: '▬\n▬' },
            ] as const).map(({ value, label, desc, icon }) => (
              <button
                key={value}
                onClick={() => setForm((f) => ({ ...f, menuLayout: value }))}
                className={`flex-1 border-2 rounded-xl p-4 text-left transition-all ${
                  form.menuLayout === value
                    ? 'border-gray-800 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-lg mb-1 font-mono leading-tight whitespace-pre">{icon}</div>
                <p className={`text-sm font-semibold ${form.menuLayout === value ? 'text-gray-900' : 'text-gray-600'}`}>{label}</p>
                <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Tema de color */}
        <div className="border-t border-gray-100 pt-4">
          <p className="text-sm font-semibold text-gray-700 mb-1">Tema de color del menú</p>
          <p className="text-xs text-gray-400 mb-3">Se aplica en botones y acentos del menú digital del comensal</p>
          <div className="flex flex-wrap gap-2">
            {THEMES.map((t) => {
              const active = form.colorTheme === t.id
              return (
                <button
                  key={t.id}
                  title={t.name}
                  onClick={() => setForm((f) => ({ ...f, colorTheme: t.id }))}
                  style={{ backgroundColor: t.primary }}
                  className={`w-8 h-8 rounded-full transition-all ${active ? 'ring-2 ring-offset-2 ring-gray-700 scale-110' : 'opacity-60 hover:opacity-90 hover:scale-105'}`}
                />
              )
            })}
          </div>
          {(() => {
            const active = THEMES.find((t) => t.id === form.colorTheme)
            return active ? (
              <p className="text-xs text-gray-500 mt-2">
                <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ backgroundColor: active.primary }} />
                {active.name}
              </p>
            ) : null
          })()}
        </div>

        <SaveBar />
      </div>
    </Card>
  )

  // ── Pestaña: Mensajes ────────────────────────────────────────────────────
  if (activeTab === 'mensajes') return (
    <Card>
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="font-bold text-gray-900">Mensajes</h2>
        <p className="text-xs text-gray-400 mt-0.5">Textos que se muestran al comensal durante su experiencia</p>
      </div>
      <div className="p-6 space-y-6">
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Nota para órdenes para llevar</p>
          <p className="text-xs text-gray-400 mb-2">Aparece debajo del campo de nombre cuando el cliente escanea una mesa para llevar.</p>
          <Input
            value={form.takeoutNameHint}
            onChange={(e) => setForm((f) => ({ ...f, takeoutNameHint: e.target.value }))}
            placeholder="Ej: Incluye tu número de ticket si ya pasaste a pagar"
            maxLength={200}
          />
        </div>

        <div className="border-t border-gray-100 pt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Mensajes rápidos al llamar mesero</p>
          <p className="text-xs text-gray-400 mb-3">Máximo 6 mensajes. Los que estén vacíos no se mostrarán al cliente.</p>
          <div className="space-y-2">
            {form.quickMessages.map((msg, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-gray-400 w-4">{i + 1}.</span>
                <Input
                  value={msg}
                  onChange={(e) => {
                    const next = [...form.quickMessages]
                    next[i] = e.target.value
                    setForm((f) => ({ ...f, quickMessages: next }))
                  }}
                  placeholder={`Mensaje rápido ${i + 1}`}
                />
              </div>
            ))}
          </div>
        </div>

        <SaveBar />
      </div>
    </Card>
  )

  // ── Pestaña: Módulos ──────────────────────────────────────────────────────
  if (activeTab === 'modulos') return (
    <Card>
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="font-bold text-gray-900">Módulos y funcionalidades</h2>
      </div>
      <div className="p-6 space-y-4">

        {/* Operación */}
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Operación</p>

        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-800">Activación de mesa requerida</p>
            <p className="text-xs text-gray-400 mt-0.5">El mesero debe habilitar la mesa antes de que el cliente pueda ver el menú.</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.requireTableActivation}
            onClick={() => setForm((f) => ({ ...f, requireTableActivation: !f.requireTableActivation }))}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${form.requireTableActivation ? 'bg-gray-900' : 'bg-gray-200'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.requireTableActivation ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-gray-100 pt-4">
          <div>
            <p className="text-sm font-medium text-gray-800">Autoservicio (sin meseros)</p>
            <p className="text-xs text-gray-400 mt-0.5">Las órdenes van directo a cocina sin requerir confirmación de mesero.</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.selfService}
            onClick={() => setForm((f) => ({ ...f, selfService: !f.selfService }))}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${form.selfService ? 'bg-gray-900' : 'bg-gray-200'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.selfService ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {/* Plan PRO */}
        <div className="border-t border-gray-100 pt-4 space-y-1">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Plan PRO</p>
          <PlanToggle
            label="Evaluación de platillos"
            description="Permite que los comensales evalúen su experiencia al ser servidos"
            value={form.ratingsEnabled}
            onChange={() => setForm((f) => ({ ...f, ratingsEnabled: !f.ratingsEnabled }))}
            feature="ratings"
            plan={restaurant?.plan}
          />
          {form.ratingsEnabled && planAllows(restaurant?.plan, 'ratings') && (
            <div className="pl-1 pb-2">
              <Field label="Puntos de bonificación por evaluación">
                <input
                  type="number"
                  min={0}
                  value={form.ratingBonusPoints}
                  onChange={(e) => setForm((f) => ({ ...f, ratingBonusPoints: Number(e.target.value) }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  placeholder="0"
                />
              </Field>
            </div>
          )}
          <PlanToggle
            label="Programa de lealtad"
            description="Permite acumular puntos por cada platillo pedido"
            value={form.loyaltyEnabled}
            onChange={() => setForm((f) => ({ ...f, loyaltyEnabled: !f.loyaltyEnabled }))}
            feature="loyalty"
            plan={restaurant?.plan}
          />
        </div>

        {/* Plan Enterprise */}
        <div className="border-t border-gray-100 pt-4 space-y-1">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Plan Enterprise</p>
          <PlanToggle
            label="Módulo de Reservaciones"
            description="Activa la gestión de reservaciones para Admin y Meseros"
            value={form.reservationsEnabled}
            onChange={() => setForm((f) => ({ ...f, reservationsEnabled: !f.reservationsEnabled }))}
            feature="reservations"
            plan={restaurant?.plan}
          />
          <PlanToggle
            label="Activar valet parking"
            description="Permite asignar boletos de valet a mesas y coordinar la entrega del auto"
            value={form.valetEnabled}
            onChange={() => setForm((f) => ({ ...f, valetEnabled: !f.valetEnabled }))}
            feature="valet"
            plan={restaurant?.plan}
          />
          <PlanToggle
            label="Activar lista de espera"
            description="Permite al rol Hostess gestionar la lista de espera y ver mesas disponibles"
            value={form.waitlistEnabled}
            onChange={() => setForm((f) => ({ ...f, waitlistEnabled: !f.waitlistEnabled }))}
            feature="waitlist"
            plan={restaurant?.plan}
          />
          <PlanToggle
            label="Impresión de tickets"
            description="Imprime automáticamente al confirmar órdenes y al cobrar"
            value={form.printingEnabled}
            onChange={() => setForm((f) => ({ ...f, printingEnabled: !f.printingEnabled }))}
            feature="printing"
            plan={restaurant?.plan}
          />
          <PlanToggle
            label="Cargo al cuarto habilitado"
            description="El huésped puede cargar su consumo a la cuenta de la habitación para pagar al check-out"
            value={form.roomChargeEnabled}
            onChange={() => setForm((f) => ({ ...f, roomChargeEnabled: !f.roomChargeEnabled }))}
            feature="roomCharge"
            plan={restaurant?.plan}
          />
        </div>

        {/* Ubicaciones del hotel — solo visible si venueType es hospedaje */}
        {(form.venueType === 'HOTEL' || form.venueType === 'APARTMENT' || form.venueType === 'HOSPITAL') && (
          <div className="border-t border-gray-100 pt-4 mt-2">
            <p className="text-sm font-semibold text-gray-700 mb-1">Ubicaciones disponibles para huéspedes</p>
            <p className="text-xs text-gray-400 mb-3">El huésped selecciona dónde se encuentra al hacer su pedido. "En el cuarto" siempre aparece como opción predeterminada.</p>
            <div className="space-y-2">
              {form.hotelLocations.map((loc, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={loc}
                    placeholder={`Ubicación ${i + 1} (ej. En la alberca)`}
                    onChange={(e) => setForm((f) => {
                      const locs = [...f.hotelLocations]
                      locs[i] = e.target.value
                      return { ...f, hotelLocations: locs }
                    })}
                  />
                  <button
                    type="button"
                    className="text-red-400 hover:text-red-600 px-2 text-lg font-bold"
                    onClick={() => setForm((f) => ({ ...f, hotelLocations: f.hotelLocations.filter((_, j) => j !== i) }))}
                  >×</button>
                </div>
              ))}
              <button
                type="button"
                className="text-sm text-blue-600 hover:text-blue-800 font-medium mt-1"
                onClick={() => setForm((f) => ({ ...f, hotelLocations: [...f.hotelLocations, ''] }))}
              >+ Agregar ubicación</button>
            </div>
          </div>
        )}

        <SaveBar />
      </div>
    </Card>
  )

  // ── Pestaña: IA ──────────────────────────────────────────────────────────
  if (activeTab === 'ia') return (
    <Card>
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="font-bold text-gray-900">Agente de IA — TEFFY AI</h2>
        <p className="text-xs text-gray-400 mt-0.5">Asistente virtual que responde preguntas de los comensales sobre el menú y su orden</p>
      </div>
      <div className="p-6 space-y-4">
        <PlanToggle
          label="Activar agente de IA"
          description="Habilita el chat con el asistente en el menú digital del comensal"
          value={form.aiAgentEnabled}
          onChange={() => setForm((f) => ({ ...f, aiAgentEnabled: !f.aiAgentEnabled }))}
          feature="aiAgent"
          plan={restaurant?.plan}
        />

        {form.aiAgentEnabled && planAllows(restaurant?.plan, 'aiAgent') && (
          <div className="space-y-4 pt-2">
            <Field label="Nombre del asistente">
              <Input
                value={form.aiAgentName}
                onChange={(e) => setForm((f) => ({ ...f, aiAgentName: e.target.value }))}
                placeholder="Ej: Sofía, Max, Asistente"
                maxLength={40}
              />
            </Field>

            <Field label="Tono">
              <div className="flex gap-2">
                {([
                  { value: 'FRIENDLY', label: 'Amigable' },
                  { value: 'FORMAL',   label: 'Formal' },
                  { value: 'CASUAL',   label: 'Casual' },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, aiAgentTone: opt.value }))}
                    className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      form.aiAgentTone === opt.value
                        ? 'bg-purple-600 border-purple-600 text-white'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Idioma">
              <div className="flex gap-2">
                {([
                  { value: 'ES',        label: 'Español' },
                  { value: 'EN',        label: 'Inglés' },
                  { value: 'BILINGUAL', label: 'Bilingüe' },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, aiAgentLanguage: opt.value }))}
                    className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      form.aiAgentLanguage === opt.value
                        ? 'bg-purple-600 border-purple-600 text-white'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </Field>

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Especialidades / platillos a destacar</label>
              <p className="text-xs text-gray-400">El asistente los mencionará proactivamente en sus recomendaciones</p>
              <textarea
                value={form.aiAgentHighlights}
                onChange={(e) => setForm((f) => ({ ...f, aiAgentHighlights: e.target.value }))}
                placeholder="Ej: Nuestro platillo estrella es el Caldo de res."
                maxLength={500}
                rows={3}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              />
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Instrucciones adicionales</label>
              <p className="text-xs text-gray-400">Restricciones, políticas o comportamientos específicos</p>
              <textarea
                value={form.aiAgentInstructions}
                onChange={(e) => setForm((f) => ({ ...f, aiAgentInstructions: e.target.value }))}
                placeholder="Ej: No menciones precios de happy hour."
                maxLength={500}
                rows={3}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              />
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Mensaje de bienvenida</label>
              <p className="text-xs text-gray-400">Primera frase visible al abrir el chat</p>
              <Input
                value={form.aiAgentWelcome}
                onChange={(e) => setForm((f) => ({ ...f, aiAgentWelcome: e.target.value }))}
                placeholder="Ej: ¡Hola! Soy Sofía, ¿en qué te puedo ayudar hoy?"
                maxLength={200}
              />
            </div>
          </div>
        )}

        <SaveBar />
      </div>
    </Card>
  )

  // ── Pestaña: Evento ───────────────────────────────────────────────────────
  if (activeTab === 'evento') return (
    <Card>
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="font-bold text-gray-900">Configuración de eventos</h2>
      </div>
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-900">Mostrar platillos restantes</p>
            <p className="text-xs text-gray-500">El comensal verá cuántas porciones quedan de cada platillo</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.showRemainingStock}
            onClick={() => setForm((f) => ({ ...f, showRemainingStock: !f.showRemainingStock }))}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${form.showRemainingStock ? 'bg-gray-900' : 'bg-gray-200'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.showRemainingStock ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {form.showRemainingStock && (
          <div className="pl-1">
            <Field label="Mostrar indicador a partir de (vacío = siempre)">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  value={form.lowStockThreshold ?? ''}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    lowStockThreshold: e.target.value === '' ? null : Number(e.target.value),
                  }))}
                  placeholder="Sin límite"
                  className="w-32"
                />
                <span className="text-xs text-gray-500">porciones restantes</span>
              </div>
            </Field>
            <p className="text-xs text-gray-400 mt-1">
              {form.lowStockThreshold
                ? `El indicador aparecerá cuando queden ${form.lowStockThreshold} o menos porciones.`
                : 'El indicador siempre será visible para todos los platillos con stock configurado.'}
            </p>
          </div>
        )}

        <Field label="Platillos contratados (vacío = sin límite)">
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              value={form.contractedDishes ?? ''}
              onChange={(e) => setForm((f) => ({
                ...f,
                contractedDishes: e.target.value === '' ? null : Number(e.target.value),
              }))}
              placeholder="Sin límite"
              className="w-32"
            />
            <span className="text-xs text-gray-500">platillos en total</span>
          </div>
        </Field>

        <SaveBar />
      </div>
    </Card>
  )

  // ── Pestaña: Fiscal ───────────────────────────────────────────────────────
  return (
    <Card>
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="font-bold text-gray-900">Datos fiscales y bancarios</h2>
      </div>
      <div className="p-6 space-y-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Datos fiscales</p>
        <Field label="Razón Social">
          <Input
            value={form.razonSocial}
            onChange={(e) => setForm((f) => ({ ...f, razonSocial: e.target.value }))}
            placeholder="Nombre o razón social del contribuyente"
          />
        </Field>
        <Field label="RFC">
          <Input
            value={form.rfc}
            onChange={(e) => setForm((f) => ({ ...f, rfc: e.target.value.toUpperCase() }))}
            placeholder="XXXX000000XXX"
          />
        </Field>
        <Field label="Régimen Fiscal">
          <Input
            value={form.regimenFiscal}
            onChange={(e) => setForm((f) => ({ ...f, regimenFiscal: e.target.value }))}
            placeholder="Ej. 601 - General de Ley Personas Morales"
          />
        </Field>
        <Field label="Dirección Fiscal">
          <Input
            value={form.direccionFiscal}
            onChange={(e) => setForm((f) => ({ ...f, direccionFiscal: e.target.value }))}
            placeholder="Calle, número, colonia, municipio, CP"
          />
        </Field>
        <Field label="URL para Facturación">
          <Input
            value={form.urlFacturacion}
            onChange={(e) => setForm((f) => ({ ...f, urlFacturacion: e.target.value }))}
            placeholder="https://facturacion.ejemplo.com"
          />
        </Field>

        <div className="border-t border-gray-100 pt-4 space-y-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Datos para transferencia</p>
          <Field label="Número de cuenta / CLABE">
            <Input
              value={form.transferAccountNumber}
              onChange={(e) => setForm((f) => ({ ...f, transferAccountNumber: e.target.value }))}
              placeholder="Ej. 012180001234567895"
            />
          </Field>
          <Field label="Nombre del titular de la cuenta">
            <Input
              value={form.transferAccountHolder}
              onChange={(e) => setForm((f) => ({ ...f, transferAccountHolder: e.target.value }))}
              placeholder="Nombre completo o razón social del titular"
            />
          </Field>
        </div>

        <SaveBar />
      </div>
    </Card>
  )
}

// ─── Perfil admin ─────────────────────────────────────────────────────────────
function ProfileSection() {
  const { updateProfile } = useAuthStore()

  const [form, setForm] = useState({ name: '', email: '', currentPassword: '', newPassword: '', confirmPassword: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState(false)

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: async () => { const { data } = await api.get('/auth/me'); return data },
    staleTime: 0,
  })

  useEffect(() => {
    if (me) setForm((f) => ({ ...f, name: me.name ?? '', email: me.email ?? '' }))
  }, [me])

  const mutation = useMutation({
    mutationFn: async () => {
      const errs: Record<string, string> = {}
      if (form.newPassword && form.newPassword !== form.confirmPassword)
        errs.confirmPassword = 'Las contraseñas no coinciden'
      if (form.newPassword && form.newPassword.length < 8)
        errs.newPassword = 'Mínimo 8 caracteres'
      if (form.newPassword && !form.currentPassword)
        errs.currentPassword = 'Ingresa tu contraseña actual'
      if (Object.keys(errs).length) { setErrors(errs); throw new Error('Validación') }
      setErrors({})

      const payload: Record<string, string> = {}
      if (form.name && form.name !== me?.name) payload.name = form.name
      if (form.email && form.email !== me?.email) payload.email = form.email
      if (form.newPassword) {
        payload.currentPassword = form.currentPassword
        payload.newPassword = form.newPassword
      }
      const { data } = await api.patch('/auth/me', payload)
      return data
    },
    onSuccess: (data) => {
      updateProfile({ userName: data.name })
      setForm((f) => ({ ...f, currentPassword: '', newPassword: '', confirmPassword: '' }))
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
  })

  return (
    <Card>
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="font-bold text-gray-900">Mi cuenta</h2>
      </div>
      <div className="p-6 space-y-4">
        <Field label="Nombre" error={errors.name}>
          <Input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Tu nombre"
          />
        </Field>
        <Field label="Correo electrónico" error={errors.email}>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="admin@restaurante.com"
          />
        </Field>

        <div className="border-t border-gray-100 pt-4">
          <p className="text-xs text-gray-400 mb-3 font-medium uppercase tracking-wide">
            Cambiar contraseña (opcional)
          </p>
          <div className="space-y-3">
            <Field label="Contraseña actual" error={errors.currentPassword}>
              <Input
                type="password"
                value={form.currentPassword}
                onChange={(e) => setForm((f) => ({ ...f, currentPassword: e.target.value }))}
                placeholder="••••••••"
              />
            </Field>
            <Field label="Nueva contraseña" error={errors.newPassword}>
              <Input
                type="password"
                value={form.newPassword}
                onChange={(e) => setForm((f) => ({ ...f, newPassword: e.target.value }))}
                placeholder="Mínimo 8 caracteres"
              />
            </Field>
            <Field label="Confirmar nueva contraseña" error={errors.confirmPassword}>
              <Input
                type="password"
                value={form.confirmPassword}
                onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                placeholder="••••••••"
              />
            </Field>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>
            Guardar cambios
          </Button>
          {saved && <span className="text-sm text-green-600 font-medium">✓ Guardado</span>}
          {mutation.isError && mutation.error?.message !== 'Validación' && (
            <span className="text-sm text-red-500">{(mutation.error as Error).message}</span>
          )}
        </div>
      </div>
    </Card>
  )
}

// ─── PlanToggle ───────────────────────────────────────────────────────────────
function PlanToggle({
  label, description, value, onChange, feature, plan,
}: {
  label: string
  description: string
  value: boolean
  onChange: () => void
  feature: string
  plan: PlanType | undefined
}) {
  const allowed = planAllows(plan, feature)
  return (
    <div className={`flex items-center justify-between py-3 border-t border-gray-100 ${!allowed ? 'opacity-60' : ''}`}>
      <div>
        <p className="text-sm font-medium text-gray-800">{label}</p>
        <p className="text-xs text-gray-400 mt-0.5">{description}</p>
        {!allowed && (
          <p className="text-xs text-amber-600 font-medium mt-1">⚠ {FEATURE_UPGRADE[feature]}</p>
        )}
      </div>
      <button
        type="button"
        onClick={allowed ? onChange : undefined}
        disabled={!allowed}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none
          ${!allowed ? 'cursor-not-allowed opacity-50' : ''}
          ${value && allowed ? 'bg-gray-900' : 'bg-gray-200'}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value && allowed ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  )
}
